import {
  useEnsureRoom,
  useLocalParticipant,
  useParticipantInfo,
  useParticipants,
  useRoomContext,
} from '@livekit/components-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFlag } from '@rsos/flags';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { LiveVideoService } from './LiveVideoService';
import { SharedCallerState, defaultSharedCallerState } from './constants';
import { useSessionPersistentState } from './useSessionPersistentState';

export function useSharedCallerStateForDispatcher(): [
  SharedCallerState,
  (newValue: Partial<SharedCallerState>) => void,
] {
  const useNewService = useFlag('use-new-vt911');
  const orgName = useCurrentOrgName();
  const room = useRoomContext();
  const participants = useParticipants();
  const caller = participants.find(
    p => JSON.parse(p.metadata || '{}').role === 'caller',
  );
  const { metadata } = useParticipantInfo({ participant: caller });
  const sharedCallerState: SharedCallerState = useMemo(() => {
    return {
      ...defaultSharedCallerState,
      ...JSON.parse(metadata || '{}'),
    };
  }, [metadata]);

  const api = useMemo(
    () => new LiveVideoService(orgName, true, useNewService),
    [orgName, useNewService],
  );

  // QUESTION: does this need to be done optimistically to feel responsive?
  const setSharedCallerState = useCallback(
    async (newState: Partial<SharedCallerState>) => {
      if (!caller) {
        // eslint-disable-next-line no-console
        console.error(
          'Attempted to set caller state when caller is not present',
        );
        return;
      }

      await api.updateCallerMetadata(room?.name, caller.identity, newState);
    },
    [api, caller, room],
  );

  return [sharedCallerState, setSharedCallerState];
}

export function useSharedCallerStateForCaller(): [
  SharedCallerState,
  (newValue: Partial<SharedCallerState>) => Promise<void>,
] {
  const { localParticipant } = useLocalParticipant();
  const { metadata } = useParticipantInfo({ participant: localParticipant });
  const [sharedCallerState, setSharedCallerState] = useSessionPersistentState({
    key: 'sharedCallerState',
    defaultValue: defaultSharedCallerState,
  });
  const [initialized, setInitialized] = useState(false);
  const room = useEnsureRoom();

  // Do this whenever we recieve updated metadata from livekit:
  useEffect(() => {
    if (!metadata) return;

    const handleMetadataUpdate = async () => {
      // The first time we recieve participant metadata, ignore it, and override
      // with our stored values; this allows any local settings to persist across
      // a refresh.
      if (!initialized) {
        try {
          await localParticipant.setMetadata(JSON.stringify(sharedCallerState));
          setInitialized(true);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to set localParticipant metadata:', error);
        }
        // Any other time we recieve changed metadata, it's instructions from UNITE;
        // overwrite our version with the update.
      } else {
        setSharedCallerState({
          ...sharedCallerState,
          ...decodeMetadata(metadata),
        });
      }
    };

    handleMetadataUpdate().catch(error =>
      // eslint-disable-next-line no-console
      console.error('Error handling metadata update:', error),
    );
    // Do NOT run this effect when sharedCallerState updates; that'd be an infinite loop!
    // TODO: maybe use the functional version of setShared etc?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata, initialized, setSharedCallerState]);

  const updateSharedCallerState = useCallback(
    async (newState: Partial<SharedCallerState>) => {
      if (room.state !== 'connected') {
        // Prevent localParticipant.setMetadata exception when state updates
        // happen after the room is disconnected.
        // eslint-disable-next-line no-console
        console.debug(
          'Skipping updateSharedCallerState because room is not connected',
        );
        return;
      }

      const update = {
        // use localParticipant.metadata, here, rather than sharedCallerState,
        // in order to satisfy react; we don't want to rebuild this function
        // every time metadata changes (that will cause infinite loops
        // elsewhere) but we also need fresh metadata, not whatever was enclosed
        // the last time this function was built.
        ...defaultSharedCallerState,
        ...decodeMetadata(localParticipant.metadata),
        ...newState,
      };

      try {
        await localParticipant.setMetadata(JSON.stringify(update));
        setSharedCallerState(update);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          'Failed to update localParticipant metadata. Cause:',
          error,
        );
      }
    },
    [localParticipant, setSharedCallerState],
  );

  return [sharedCallerState, updateSharedCallerState];
}

function decodeMetadata(metadata?: string): Partial<SharedCallerState> {
  return JSON.parse(metadata || '{}');
}
